import { useQuery } from "react-query";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";

type GetAllSectorsOptions = {
    page?: number;
    itemsPerPage?: number;
};

export const useGetAllSectors = (options: GetAllSectorsOptions = {}) => {
    const { page = 1, itemsPerPage = 20 } = options;

    const query = useQuery({
        queryKey: ["sectorsValue", page, itemsPerPage],
        queryFn: async () => {
            const response = await loggedInApiCall.get("/api/organization_sectors", {
                params: {
                    page,
                    itemsPerPage,
                },
            });
            return response;
        },
        select: (request) => {
            const companySectors = request.data["hydra:member"];
            const totalItems = request.data["hydra:totalItems"];
            const data = zodParseWithLog(companySectors, CompanySectorZod.array(), "companySectors");
            return {
                companySectors: data,
                totalItems,
            };
        },
    });

    return {
        companySectors: query.data?.companySectors || [],
        totalItems: query.data?.totalItems || 0,
        ...query,
    };
};

// Hook for searching company sectors with debounced search
export const useSearchCompanySectors = (searchTerm?: string, options?: { enabled?: boolean }) => {
    const { enabled = true } = options || {};

    const query = useQuery({
        queryKey: ["sectorsSearch", searchTerm],
        queryFn: async () => {
            const params: Record<string, any> = {
                itemsPerPage: 100, // Limit results for autocomplete
            };

            if (searchTerm && searchTerm.length >= 3) {
                params.name = searchTerm; // Use name filter for search
            }

            const response = await loggedInApiCall.get("/api/organization_sectors", { params });
            return response;
        },
        select: (request) => {
            const companySectors = request.data["hydra:member"];
            const data = zodParseWithLog(companySectors, CompanySectorZod.array(), "companySectorsSearch");
            return data;
        },
        enabled: enabled && (!searchTerm || searchTerm.length >= 3), // Only search if term is 3+ chars
        staleTime: 30000, // Cache for 30 seconds
    });

    return {
        companySectors: query.data || [],
        ...query,
    };
};

export type Company = {
    id: number;
    name: string;
    updatedAt?: string;
    canView?: boolean;
    canModify?: boolean;
    canDelete?: boolean;
};

export type CompanySectorSsq = {
    ssqId: number;
    position: number;
    ssqTitle?: string; // For display purposes
    headings?: Array<{
        hidden?: boolean;
        title: string;
    }>;
};

export type CompanySector = {
    "@id"?: string;
    id: number;
    name: string;
    ssqs?: CompanySectorSsq[]; // Multiple SSQs with positions
    primarySsqId?: number; // Primary SSQ (position 1)
    ssqId?: number; // Primary SSQ (position 1)
    variationIds?: number[];
    defaultVariationIds?: number[];
    updatedAt?: string;
    organizations?: Company[];
    canView?: boolean;
    canModify?: boolean;
    canDelete?: boolean;
    headings?: Array<{
        hidden?: boolean;
        title: string;
    }>;
};

export const CompanyZod: z.ZodSchema<Company> = z.object({
    id: z.number(),
    name: z.string(),
    updatedAt: z.string().optional(),
    canView: z.boolean().optional(),
    canModify: z.boolean().optional(),
    canDelete: z.boolean().optional(),
});

export const CompanySectorSsqZod: z.ZodSchema<CompanySectorSsq> = z.object({
    ssqId: z.number(),
    position: z.number(),
    ssqTitle: z.string().optional(),
    headings: z
        .array(
            z.object({
                hidden: z.boolean().optional(),
                title: z.string(),
            }),
        )
        .optional(),
});

export const CompanySectorZod: z.ZodSchema<CompanySector> = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    name: z.string(),
    ssqs: CompanySectorSsqZod.array().optional(),
    primarySsqId: z.number().optional(),
    ssqId: z
        .number()
        .nullish()
        .transform((value) => value ?? undefined),
    variationIds: z.number().array().optional(),
    defaultVariationIds: z.number().array().optional(),
    updatedAt: z.string().optional(),
    organizations: CompanyZod.array().optional(),
    canView: z.boolean().optional(),
    canModify: z.boolean().optional(),
    canDelete: z.boolean().optional(),
    headings: z
        .array(
            z.object({
                hidden: z.boolean().optional(),
                title: z.string(),
            }),
        )
        .optional(),
});

export type CompanySectorForm = {
    id?: number;
    name: string;
    ssqs?: Array<{ ssqId: number; position: number }>; // Multiple SSQs
    ssqsData?: Array<{ ssqId: number; position: number }>; // For backend processing
    variationIds?: number[];
    defaultVariationIds?: number[];
    variationsOverriden: boolean;
    updatedAt?: string;
    canView?: boolean;
    canModify?: boolean;
    canDelete?: boolean;
    headings?: Array<{
        hidden?: boolean;
        title: string;
    }>;
};
